import { useMemo } from 'react';
import * as fmt from '../lib/format.js';
import {
  GOAL_LABELS,
  KCAL_PER_KG_WEEK,
  TREND_WINDOWS,
  daysToResolve,
  estimateEnergy,
  goalBand,
  linePath,
  trendAt,
  verdict,
  type BodyProfile,
  type DayRow,
  type Goal,
} from '../lib/stats.js';

interface Props {
  series: DayRow[];
  selectedIndex: number;
  goal: Goal;
  profile: BodyProfile;
  window: number;
  onWindowChange: (window: number) => void;
  onGoalChange: (goal: Goal) => void;
}

const GOALS: Goal[] = ['loss', 'maintain', 'gain'];
const SPARK_WIDTH = 100;
const SPARK_HEIGHT = 40;

export function TrendCard({
  series,
  selectedIndex,
  goal,
  profile,
  window,
  onWindowChange,
  onGoalChange,
}: Props) {
  const trend = useMemo(
    () => trendAt(series, selectedIndex, window),
    [series, selectedIndex, window],
  );
  // Fenêtre de référence et non `window` : changer la loupe de la tendance ne
  // doit pas changer le maintien affiché ici et ailleurs.
  const energy = useMemo(
    () => estimateEnergy(series, selectedIndex, profile),
    [series, selectedIndex, profile],
  );

  // La bande est un pourcentage du poids : il faut donc un poids pour la
  // convertir en kg/semaine. Le poids lissé de la fenêtre, à défaut le repère
  // de l'inscription — annoncer une cible calculée sur un poids de convention
  // serait faux dès le premier écran d'un compte neuf.
  const band = goalBand(goal, trend.ok ? trend.weight : profile.startWeightKg);

  const spark = useMemo(() => {
    if (!trend.ok) return null;
    const known = trend.values.filter((value): value is number => value != null);
    if (known.length === 0) return null;

    const low = Math.min(...known, ...trend.fitted) - 0.15;
    const high = Math.max(...known, ...trend.fitted) + 0.15;
    const span = high - low || 1;
    const last = trend.values.length - 1 || 1;
    const toY = (value: number) => SPARK_HEIGHT - ((value - low) / span) * SPARK_HEIGHT;

    return {
      path: linePath(trend.values, low, high, SPARK_WIDTH, SPARK_HEIGHT),
      // La droite ajustée superposée au nuage : c'est elle que le chiffre
      // annonce, et elle seule permet de voir si le nuage la justifie.
      fit: { y1: toY(trend.fitted[0]), y2: toY(trend.fitted[1]) },
      // Au-delà d'une quinzaine de points, les pastilles se chevauchent.
      dots:
        trend.values.length > 15
          ? []
          : trend.values
              .map((value, index) =>
                value == null ? null : { key: index, x: (index / last) * SPARK_WIDTH, y: toY(value) },
              )
              .filter((dot): dot is { key: number; x: number; y: number } => dot !== null),
    };
  }, [trend]);

  const assessment = trend.ok ? verdict(trend.perWeek, band, goal, trend.marginPerWeek) : null;
  const color = assessment?.color ?? 'var(--ink3)';

  // Ne se calcule que lorsqu'il a quelque chose à dire : tant que la bande
  // tranche, annoncer un délai n'apporterait rien.
  const resolution = useMemo(
    () =>
      trend.ok && assessment?.kind === 'unknown'
        ? daysToResolve(series, selectedIndex, band, trend)
        : null,
    [trend, assessment?.kind, series, selectedIndex, band],
  );

  /**
   * Le conseil calorique part de la dépense *observée* — apports déclarés
   * corrigés de la variation de poids — et non d'une formule. Il reste donc
   * exprimé dans l'unité de comptage de l'utilisateur, sous-déclaration
   * comprise : c'est ce qui le rend actionnable.
   */
  const advice = useMemo(() => {
    if (!trend.ok || !assessment) return null;
    const maintenance = `Maintien ≈ ${fmt.number(energy.tdee)} kcal/j · ${
      energy.source === 'observed'
        ? `déduit de ${energy.days} j de saisies`
        : energy.restingSource === 'mifflin'
          ? 'modèle morphologie + pas, faute de saisies'
          : 'modèle approximatif — profil incomplet'
    }`;

    if (assessment.kind === 'unknown') {
      return [maintenance, 'Marge trop large pour conseiller un ajustement chiffré'];
    }
    if (assessment.kind === 'in') {
      return [maintenance, `Apports à conserver ≈ ${fmt.number(energy.intake)} kcal/j`];
    }
    const correction = (band.target - trend.perWeek) * KCAL_PER_KG_WEEK;
    return [maintenance, `Ajuste d’environ ${fmt.signedInteger(correction)} kcal/j`];
  }, [trend, assessment, energy, band.target]);

  return (
    <section className="card">
      <header className="trend__head">
        <h2 className="eyebrow" style={{ margin: 0 }}>
          Tendance du poids
        </h2>
        <div className="segmented" role="group" aria-label="Fenêtre d’observation">
          {TREND_WINDOWS.map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={window === option}
              onClick={() => onWindowChange(option)}
            >
              {option} j
            </button>
          ))}
        </div>
        <div className="segmented" role="group" aria-label="Objectif">
          {GOALS.map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={goal === option}
              onClick={() => onGoalChange(option)}
            >
              {GOAL_LABELS[option]}
            </button>
          ))}
        </div>
      </header>

      <p className="hint" style={{ marginBottom: 12 }}>
        {goal === 'maintain'
          ? `Cible : poids stable, bande ±${fmt.weight(band.high, 2)} kg/sem (±${fmt.signedPercent(band.definition.high).slice(1)} du poids).`
          : `Cible : ${fmt.signedPercent(band.definition.target)}/sem du poids, soit ${fmt.delta(band.target, 2)} kg/sem à ${fmt.weight(band.weight)} kg. Bande tolérée ${fmt.delta(band.low, 2)} à ${fmt.delta(band.high, 2)}.`}
      </p>

      {trend.ok && spark ? (
        <div className="trend__body">
          <div>
            <div className="trend__value">
              <strong style={{ color }}>{fmt.delta(trend.perWeek, 2)}</strong>
              <span>kg / sem</span>
            </div>
            <p
              className="trend__margin"
              title={
                trend.autocorrelation > 0
                  ? `Résidus corrélés d’un jour à l’autre (ρ = ${fmt.weight(trend.autocorrelation, 2)}) : ${trend.points} pesées n’en valent que ${Math.round(trend.effectiveN)} indépendantes, et la marge est élargie de ${Math.round((trend.inflation - 1) * 100)} %.`
                  : 'Aucune corrélation détectable entre résidus consécutifs.'
              }
            >
              ± {fmt.weight(trend.marginPerWeek, 2)} (IC 95 %) ·{' '}
              {fmt.signedPercent(trend.perWeek / trend.weight)}/sem
            </p>
            {trend.gym ? (
              <p
                className="trend__gym"
                title="Amplitude du creux ou de la bosse laissée sur la balance par une séance, étalée sur les trois pesées qui la suivent."
              >
                Effet séance {fmt.delta(trend.gym.effect, 2)} kg ± {fmt.weight(trend.gym.margin, 2)}
              </p>
            ) : null}
            {resolution ? (
              <p className="trend__gym">
                {resolution.days === 0
                  ? `La fenêtre ${resolution.window} j tranche déjà`
                  : `Encore ~${resolution.days} j de pesée quotidienne pour trancher${
                      resolution.window === trend.window ? '' : ` (fenêtre ${resolution.window} j)`
                    }`}
              </p>
            ) : null}
            <p className="trend__verdict">
              {assessment?.text} · {trend.points} pesées sur {trend.window} jours
              {trend.autocorrelation > 0 && Math.round(trend.effectiveN) >= 2
                ? `, soit ${Math.round(trend.effectiveN)} indépendantes (ρ = ${fmt.weight(trend.autocorrelation, 2)})`
                : ''}
            </p>
            {advice?.map((line) => (
              <p className="trend__kcal" key={line}>
                {line}
              </p>
            ))}
          </div>

          <svg
            className="trend__spark"
            viewBox={`0 0 ${SPARK_WIDTH} ${SPARK_HEIGHT}`}
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <path
              d={spark.path}
              fill="none"
              stroke="var(--hair)"
              strokeWidth="1"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
            <line
              x1="0"
              x2={SPARK_WIDTH}
              y1={spark.fit.y1}
              y2={spark.fit.y2}
              stroke={color}
              strokeWidth="1.5"
              vectorEffect="non-scaling-stroke"
            />
            {spark.dots.map((dot) => (
              <circle
                key={dot.key}
                cx={dot.x}
                cy={dot.y}
                r="2"
                fill={color}
                vectorEffect="non-scaling-stroke"
              />
            ))}
          </svg>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div className="trend__value">
            <strong style={{ color: 'var(--ink3)' }}>—</strong>
          </div>
          <p className="muted" style={{ fontSize: 12, lineHeight: 1.5, maxWidth: '30ch' }}>
            {trend.ok ? '' : trend.why}
          </p>
        </div>
      )}
    </section>
  );
}
